import { useEffect, useRef, useState } from 'react';

import { getEnvironment, isServer } from '#src/lib/utils.mjs';
import { logger } from '#src/lib/logger';

const { BRANCH_WAYBETTER_APP_KEY } = getEnvironment();

//
// Branch SDK Wrapper
//
const deepview = (data, open_app = true) =>
  new Promise((resolve, reject) => {
    // https://help.branch.io/developers-hub/docs/web-full-reference#deepviewdata-options-callback
    window.branch.deepview(
      { data },
      {
        make_new_link: false,
        open_app,
      },
      (err) => (err ? reject(err) : resolve())
    );
  });

const deepviewCta = () =>
  new Promise((resolve, reject) => {
    // https://help.branch.io/developers-hub/docs/web-full-reference#deepviewcta
    window.branch.deepviewCta((err) => (err ? reject(err) : resolve()));
  });

const init = async () =>
  new Promise((resolve, reject) => {
    try {
      if (!BRANCH_WAYBETTER_APP_KEY) {
        throw new Error('Branch SDK key is not set');
      }
      window.branch.init(
        // Key obtained in branch platform:
        // https://dashboard.branch.io/account-settings/profile
        BRANCH_WAYBETTER_APP_KEY,
        // Branch init options, see here:
        // https://help.branch.io/developers-hub/docs/web-full-reference#initbranch_key-options-callback
        { no_journeys: true },
        (err, user) => {
          err
            ? reject(err)
            : resolve({
                user,
                attemptAppOpen: deepview,
                prepareHandleDeepLinkClick: (data) => deepview(data, false),
                createLink: link,
                handleDeepLinkClick: deepviewCta,
                isDesktop: typeof window.ontouchstart === 'undefined',
                branch: window.branch,
              });
        }
      );
    } catch (error) {
      logger.error('[Branch SDK] failed to initialize', error);
      reject(error);
    }
  });

const link = (data) =>
  new Promise((resolve, reject) => {
    // https://help.branch.io/developers-hub/docs/web-full-reference#deepviewcta
    window.branch.link(
      { data: { $always_deeplink: true, ...data } },
      (err, link) => (err ? reject(err) : resolve(link))
    );
  });
// End Branch SDK Wrapper

/**
 * React hook that asyncronously initializes the Branch SDK and provides a
 * minimal API to perform deep linking behaviors:
 * - user: Info about the user from Branch perspective
 * - attemptAppOpen: Function to deep link a mobile user to the app or app store
 * - createLink: Function to create a deep link URL
 * - handleDeepLinkClick: Function to redirect to app store when app is not installed
 * - isDesktop: Boolean indicating user is on desktop (or not)
 *
 * @returns {object} Truncated Branch SDK API
 */
export const useDeepLinkApi = () => {
  const isInitialized = useRef(false);
  const isInitializing = useRef(false);
  const [api, setApi] = useState();

  useEffect(() => {
    if (isServer || isInitialized.current || isInitializing.current) {
      return;
    }

    (async () => {
      try {
        isInitializing.current = true;
        setApi(await init());
        isInitialized.current = true;
      } catch (error) {
        logger.error('[useDeepLinkApi] failed to initialize API', error);
      } finally {
        isInitializing.current = false;
      }
    })();
  }, []);

  return api;
};
